import {
  Box,
  Button,
  Divider,
  Flex,
  ModalBody,
  ModalCloseButton,
  ModalHeader,
  SimpleGrid,
  Skeleton,
  Switch,
  Text,
  useBreakpointValue,
  useToast,
} from '@chakra-ui/react'
import { useCallback, useEffect, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { PushNotifications, type PermissionStatus } from '@capacitor/push-notifications'
import handleErrorToast from '../../../components/common/toast/handleErrorToast'
import handleSuccessToast from '../../../components/common/toast/handleSuccessToast'
import { useAuth, useProfileDetails, useUpdateProfileNotifications } from '../../../hooks/useAuth'
import { useSendTestFCM } from '../../../hooks/useFCMDevices'

const capitalizeWords = (str: string) => {
  return str.replace(/\b\w/g, (char) => char.toUpperCase()).replace(/_/g, ' ')
}

const INVESTIGATION_NOTIFICATIONS = [
  'investigation_created',
  'investigation_file_created',
  'investigation_comment_created',
]

const TASKS_KEYWORD_NOTIFICATIONS = [
  'task_keyword_scheduled',
  'task_keyword_failure',
  'task_keyword_timeout',
  'task_keyword_success',
  'task_keyword_expired',
  'task_keyword_updated',
]

const TASKS_IMAGE_NOTIFICATIONS = [
  'task_image_scheduled',
  'task_image_failure',
  'task_image_timeout',
  'task_image_success',
  'task_image_expired',
  'task_image_updated',
]

const SCREENSHOTS_COMPLIANCE_NOTIFICATIONS = [
  'screenshot_scheduled',
  'compliancy_check_scheduled',
  'screenshot_deleted',
]

const UPDATES_NOTIFICATIONS = [
  'item_updated',
  'member_updated',
  'brand_updated',
  'platform_updated',
  'platform_created',
  'item_field_changed',
]

const EMAIL_NOTIFICATIONS = ['email_pending_member_review', 'email_notification_summary', 'email_low_token_balance']

const SAW_NOTIFICATIONS = ['saw_processed', 'saw_updated', 'saw_items_imported', 'saw_late_notice']

const TICKETS_NOTIFICATIONS = ['ticket_assigned', 'ticket_updated', 'ticket_deleted', 'ticket_commented', 'ticket_due']

const NotificationSettingsPage = () => {
  const toast = useToast()
  const { permissions } = useAuth()
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false

  const getFilteredNotifications = (notifications: string[]) => {
    if (permissions.isMember) {
      return notifications.filter((notif) => ['email_pending_member_review', 'email_low_token_balance'].includes(notif))
    }
    return notifications
  }

  const filteredInvestigationNotifications = getFilteredNotifications(INVESTIGATION_NOTIFICATIONS)
  const filteredTasksKeywordNotifications = getFilteredNotifications(TASKS_KEYWORD_NOTIFICATIONS)
  const filteredTasksImageNotifications = getFilteredNotifications(TASKS_IMAGE_NOTIFICATIONS)
  const filteredScreenshotsComplianceNotifications = getFilteredNotifications(SCREENSHOTS_COMPLIANCE_NOTIFICATIONS)
  const filteredUpdatesNotifications = getFilteredNotifications(UPDATES_NOTIFICATIONS)
  const filteredSawNotifications = getFilteredNotifications(SAW_NOTIFICATIONS)
  const filteredTicketsNotifications = getFilteredNotifications(TICKETS_NOTIFICATIONS)
  const filteredEmailNotifications = getFilteredNotifications(EMAIL_NOTIFICATIONS)

  const profileQuery = useProfileDetails()

  const allNotificationTypes = [
    ...filteredInvestigationNotifications,
    ...filteredTasksKeywordNotifications,
    ...filteredTasksImageNotifications,
    ...filteredScreenshotsComplianceNotifications,
    ...filteredUpdatesNotifications,
    ...filteredSawNotifications,
    ...filteredTicketsNotifications,
    ...filteredEmailNotifications,
  ]

  // Different defaults for email notifications vs others
  const getDefaultForNotificationType = (type: string): boolean => {
    if (type === 'email_low_token_balance') {
      return true
    }
    if (type.startsWith('email_')) {
      return false
    }
    return true
  }

  const defaultNotificationSettings = allNotificationTypes.reduce((acc, type) => {
    acc[type] = getDefaultForNotificationType(type)
    return acc
  }, {} as { [key: string]: boolean })

  const [notificationSettings, setNotificationSettings] = useState<{ [key: string]: boolean }>(
    defaultNotificationSettings
  )

  const { isFetching } = profileQuery
  const updateNotificationMutation = useUpdateProfileNotifications()

  const isNative = Capacitor.isNativePlatform()
  const serverPushEnabled =
    (profileQuery.data?.data?.notifications_settings?.push_enabled as boolean | null | undefined) ?? true
  const [pushEnabled, setPushEnabled] = useState<boolean>(true)
  const [pushPermission, setPushPermission] = useState<PermissionStatus['receive']>('prompt')
  const sendTestFCM = useSendTestFCM()

  useEffect(() => {
    if (profileQuery.data) setPushEnabled(serverPushEnabled)
  }, [profileQuery.data, serverPushEnabled])

  const refreshPushPermission = useCallback(async () => {
    if (!isNative) return
    try {
      const status = await PushNotifications.checkPermissions()
      setPushPermission(status.receive)
    } catch {
      setPushPermission('denied')
    }
  }, [isNative])

  useEffect(() => {
    refreshPushPermission()
  }, [refreshPushPermission])

  const handlePushEnabledChange = (checked: boolean) => {
    const previous = pushEnabled
    setPushEnabled(checked)
    updateNotificationMutation.mutate(
      { push_enabled: checked },
      {
        onError: () => {
          setPushEnabled(previous)
          handleErrorToast(toast, 'Update Failed', 'Could not update push settings. Please try again.')
        },
      },
    )
  }

  const handleSendTestFCM = () => {
    sendTestFCM.mutate(undefined, {
      onSuccess: (res) => {
        if (res.sent > 0) {
          handleSuccessToast(toast, 'Test push sent', `Delivered to ${res.sent} device(s).`)
        } else {
          handleErrorToast(toast, 'No devices', 'Open the app on a device with push enabled to register first.')
        }
      },
      onError: () => {
        handleErrorToast(toast, 'Could not send', 'Try again in a minute.')
      },
    })
  }

  const pushDenied = isNative && pushPermission === 'denied'
  const pushToggleDisabled = pushDenied || updateNotificationMutation.isPending

  useEffect(() => {
    if (profileQuery.data) {
      const serverSettings = profileQuery.data?.data?.notifications_settings || {}

      // Process server settings, handling null values properly
      const processedServerSettings = Object.keys(serverSettings).reduce((acc, key) => {
        const value = serverSettings[key]
        // Convert null to default, keep boolean values as is
        acc[key] = value === null ? getDefaultForNotificationType(key) : Boolean(value)
        return acc
      }, {} as { [key: string]: boolean })

      const mergedSettings = { ...defaultNotificationSettings, ...processedServerSettings }
      setNotificationSettings(mergedSettings)
    }
  }, [profileQuery.data])

  useEffect(() => {
    if (profileQuery.isError) {
      handleErrorToast(toast, 'Error!', 'Could not load user profile.')
    }
  }, [profileQuery.isError])

  const handleNotificationChange = (type: string, checked: boolean) => {
    const updatedSettings = {
      ...notificationSettings,
      [type]: checked,
    }
    setNotificationSettings(updatedSettings)

    const settingsToSave = { [type]: checked }

    updateNotificationMutation.mutate(settingsToSave, {
      onError: () => {
        handleErrorToast(toast, 'Update Failed', 'Could not update notification settings. Please try again.')
      },
    })
  }

  return (
    <>
      <Box
        m={{ base: 0, md: '40px 20px 20px 20px' }}
        overflowY="auto"
        sx={{
          '& > div > div .chakra-stack, & > div > div [class*="SimpleGrid"]': {},
          ...(isMobile && {
            '.notif-grid': {
              gridTemplateColumns: '1fr !important',
              gap: '8px !important',
              bg: 'white',
              borderWidth: '1px',
              borderColor: 'neutral.100',
              borderRadius: '14px',
              p: '16px',
              mb: '12px',
            },
            '.notif-section-divider': {
              display: 'none',
            },
          }),
        }}
      >
        {!isMobile && (
          <ModalHeader>
            <Flex>
              <Text fontWeight="510" textStyle="h5" mb="8px">
                Notification Settings
              </Text>
            </Flex>
            <Box display="flex" justifyContent="flex-start">
              <Text fontWeight="400" textStyle="b-sm" color="neutral.500">
                Manage your notification preferences to stay updated with the latest information.
              </Text>
            </Box>
          </ModalHeader>
        )}
        {isMobile && (
          <Box mb="20px">
            <Text fontSize="20px" fontWeight="600" color="neutral.900" lineHeight="1.3">
              Notification Settings
            </Text>
            <Text mt="4px" fontWeight="400" textStyle="b-sm" color="neutral.500">
              Manage your notification preferences to stay updated with the latest information.
            </Text>
          </Box>
        )}
        {!isMobile && <ModalCloseButton />}
        <ModalBody position="relative" overflowX="hidden" px={{ base: 0, md: '24px' }} py={{ base: 0, md: '8px' }}>
          <Box w={{ base: '100%', md: '824px' }} minH={{ base: 'auto', md: '400px' }}>
            {isNative && (
              <>
                <SimpleGrid className="notif-grid" gridTemplateColumns="repeat(2, 1fr)" gap="24px">
                  <Box>
                    <Text textStyle="b-lg" fontWeight="400" color="neutral.900">
                      Push notifications
                    </Text>
                    <Text textStyle="b-sm" color="neutral.500" mb="16px">
                      Receive push alerts on this device for new activity.
                    </Text>
                  </Box>
                  <Box>
                    <Flex alignItems="center" justifyContent="space-between" mb="8px">
                      <Flex alignItems="center">
                        <Skeleton isLoaded={!isFetching} borderRadius="40px">
                          <Switch
                            isChecked={!pushDenied && pushEnabled}
                            isDisabled={pushToggleDisabled}
                            onChange={(e) => handlePushEnabledChange(e.target.checked)}
                            colorScheme="primary"
                          />
                        </Skeleton>
                        <Text textStyle="b-sm" color="neutral.500" ml="16px">
                          Enable push notifications
                        </Text>
                      </Flex>
                    </Flex>
                    {pushDenied && (
                      <Text textStyle="b-xs" color="red.500" mb="8px">
                        Push notifications are blocked at the OS level. Enable them in your device settings to use this option.
                      </Text>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleSendTestFCM}
                      isLoading={sendTestFCM.isPending}
                      isDisabled={pushDenied || !pushEnabled}
                    >
                      Send test push
                    </Button>
                  </Box>
                </SimpleGrid>
                <Divider className="notif-section-divider" my="12px" />
              </>
            )}
            {filteredInvestigationNotifications.length > 0 && (
              <>
                <SimpleGrid className="notif-grid" gridTemplateColumns="repeat(2, 1fr)" gap="24px">
                  <Box>
                    <Text textStyle="b-lg" fontWeight="400" color="neutral.900">
                      Investigations
                    </Text>
                    <Text textStyle="b-sm" color="neutral.500" mb="16px">
                      Manage notifications related to investigations.
                    </Text>
                  </Box>
                  <Box>
                    {filteredInvestigationNotifications.map((type) => (
                      <Flex key={type} alignItems="center" justifyContent="space-between" mb="8px">
                        <Flex justifyContent="space-between" alignItems="center">
                          <Skeleton isLoaded={!isFetching} borderRadius="40px">
                            <Switch
                              isChecked={notificationSettings[type]}
                              onChange={() => handleNotificationChange(type, !notificationSettings[type])}
                              colorScheme="primary"
                            />
                          </Skeleton>
                          <Text textStyle="b-sm" color="neutral.500" ml="16px">
                            {capitalizeWords(type)}
                          </Text>
                        </Flex>
                      </Flex>
                    ))}
                  </Box>
                </SimpleGrid>
                <Divider className="notif-section-divider" my="12px" />
              </>
            )}

            {(filteredTasksKeywordNotifications.length > 0 || filteredTasksImageNotifications.length > 0) && (
              <>
                <SimpleGrid className="notif-grid" gridTemplateColumns="repeat(2, 1fr)" gap="24px">
                  <Box>
                    <Text textStyle="b-lg" fontWeight="400" color="neutral.900">
                      Tasks
                    </Text>
                    <Text textStyle="b-sm" color="neutral.500" mb="16px">
                      Manage notifications related to tasks.
                    </Text>
                  </Box>
                  <Flex direction="column">
                    {filteredTasksKeywordNotifications.length > 0 && (
                      <Box>
                        {filteredTasksKeywordNotifications.map((type) => (
                          <Flex key={type} alignItems="center" justifyContent="space-between" mb="8px">
                            <Flex justifyContent="space-between" alignItems="center">
                              <Skeleton isLoaded={!isFetching} borderRadius="40px">
                                <Switch
                                  isChecked={notificationSettings[type]}
                                  onChange={() => handleNotificationChange(type, !notificationSettings[type])}
                                  colorScheme="primary"
                                />
                              </Skeleton>
                              <Text textStyle="b-sm" color="neutral.500" ml="16px">
                                {capitalizeWords(type)}
                              </Text>
                            </Flex>
                          </Flex>
                        ))}
                      </Box>
                    )}
                    {filteredTasksKeywordNotifications.length > 0 && filteredTasksImageNotifications.length > 0 && (
                      <Divider className="notif-section-divider" my="12px" />
                    )}
                    {filteredTasksImageNotifications.length > 0 && (
                      <Box>
                        {filteredTasksImageNotifications.map((type) => (
                          <Flex key={type} alignItems="center" justifyContent="space-between" mb="8px">
                            <Flex justifyContent="space-between" alignItems="center">
                              <Skeleton isLoaded={!isFetching} borderRadius="40px">
                                <Switch
                                  isChecked={notificationSettings[type]}
                                  onChange={() => handleNotificationChange(type, !notificationSettings[type])}
                                  colorScheme="primary"
                                />
                              </Skeleton>
                              <Text textStyle="b-sm" color="neutral.500" ml="16px">
                                {capitalizeWords(type)}
                              </Text>
                            </Flex>
                          </Flex>
                        ))}
                      </Box>
                    )}
                  </Flex>
                </SimpleGrid>
                <Divider className="notif-section-divider" my="12px" />
              </>
            )}

            {filteredScreenshotsComplianceNotifications.length > 0 && (
              <>
                <SimpleGrid className="notif-grid" gridTemplateColumns="repeat(2, 1fr)" gap="24px">
                  <Box>
                    <Text textStyle="b-lg" fontWeight="400" color="neutral.900">
                      Screenshots & Compliance
                    </Text>
                    <Text textStyle="b-sm" color="neutral.500" mb="16px">
                      Manage notifications related to screenshots and compliance.
                    </Text>
                  </Box>
                  <Box>
                    {filteredScreenshotsComplianceNotifications.map((type) => (
                      <Flex key={type} alignItems="center" justifyContent="space-between" mb="8px">
                        <Flex justifyContent="space-between" alignItems="center">
                          <Skeleton isLoaded={!isFetching} borderRadius="40px">
                            <Switch
                              isChecked={notificationSettings[type]}
                              onChange={() => handleNotificationChange(type, !notificationSettings[type])}
                              colorScheme="primary"
                            />
                          </Skeleton>
                          <Text textStyle="b-sm" color="neutral.500" ml="16px">
                            {capitalizeWords(type)}
                          </Text>
                        </Flex>
                      </Flex>
                    ))}
                  </Box>
                </SimpleGrid>
                <Divider className="notif-section-divider" my="12px" />
              </>
            )}

            {filteredUpdatesNotifications.length > 0 && (
              <>
                <SimpleGrid className="notif-grid" gridTemplateColumns="repeat(2, 1fr)" gap="24px">
                  <Box>
                    <Text textStyle="b-lg" fontWeight="400" color="neutral.900">
                      Updates
                    </Text>
                    <Text textStyle="b-sm" color="neutral.500" mb="16px">
                      Manage notifications related to updates.
                    </Text>
                  </Box>
                  <Box>
                    {filteredUpdatesNotifications.map((type) => (
                      <Flex key={type} alignItems="center" justifyContent="space-between" mb="8px">
                        <Flex justifyContent="space-between" alignItems="center">
                          <Skeleton isLoaded={!isFetching} borderRadius="40px">
                            <Switch
                              isChecked={notificationSettings[type]}
                              onChange={() => handleNotificationChange(type, !notificationSettings[type])}
                              colorScheme="primary"
                            />
                          </Skeleton>
                          <Text textStyle="b-sm" color="neutral.500" ml="16px">
                            {capitalizeWords(type)}
                          </Text>
                        </Flex>
                      </Flex>
                    ))}
                  </Box>
                </SimpleGrid>
                <Divider className="notif-section-divider" my="12px" />
              </>
            )}

            {filteredSawNotifications.length > 0 && (
              <>
                <SimpleGrid className="notif-grid" gridTemplateColumns="repeat(2, 1fr)" gap="24px">
                  <Box>
                    <Text textStyle="b-lg" fontWeight="400" color="neutral.900">
                      Standalone Websites Processing
                    </Text>
                    <Text textStyle="b-sm" color="neutral.500" mb="16px">
                      Manage notifications related to Standalone Websites processing.
                    </Text>
                  </Box>
                  <Box>
                    {filteredSawNotifications.map((type) => (
                      <Flex key={type} alignItems="center" justifyContent="space-between" mb="8px">
                        <Flex justifyContent="space-between" alignItems="center">
                          <Skeleton isLoaded={!isFetching} borderRadius="40px">
                            <Switch
                              isChecked={notificationSettings[type]}
                              onChange={() => handleNotificationChange(type, !notificationSettings[type])}
                              colorScheme="primary"
                            />
                          </Skeleton>
                          <Text textStyle="b-sm" color="neutral.500" ml="16px">
                            {capitalizeWords(type)}
                          </Text>
                        </Flex>
                      </Flex>
                    ))}
                  </Box>
                </SimpleGrid>
                <Divider className="notif-section-divider" my="12px" />
              </>
            )}

            {filteredTicketsNotifications.length > 0 && (
              <>
                <SimpleGrid className="notif-grid" gridTemplateColumns="repeat(2, 1fr)" gap="24px">
                  <Box>
                    <Text textStyle="b-lg" fontWeight="400" color="neutral.900">
                      Tickets
                    </Text>
                    <Text textStyle="b-sm" color="neutral.500" mb="16px">
                      Manage notifications related to tickets.
                    </Text>
                  </Box>
                  <Box>
                    {filteredTicketsNotifications.map((type) => (
                      <Flex key={type} alignItems="center" justifyContent="space-between" mb="8px">
                        <Flex justifyContent="space-between" alignItems="center">
                          <Skeleton isLoaded={!isFetching} borderRadius="40px">
                            <Switch
                              isChecked={notificationSettings[type]}
                              onChange={() => handleNotificationChange(type, !notificationSettings[type])}
                              colorScheme="primary"
                            />
                          </Skeleton>
                          <Text textStyle="b-sm" color="neutral.500" ml="16px">
                            {capitalizeWords(type)}
                          </Text>
                        </Flex>
                      </Flex>
                    ))}
                  </Box>
                </SimpleGrid>
                <Divider className="notif-section-divider" my="12px" />
              </>
            )}

            {(permissions.isReact || permissions.isMember) && filteredEmailNotifications.length > 0 && (
              <SimpleGrid className="notif-grid" gridTemplateColumns="repeat(2, 1fr)" gap="24px">
                <Box>
                  <Text textStyle="b-lg" fontWeight="400" color="neutral.900">
                    Email
                  </Text>
                  <Text textStyle="b-sm" color="neutral.500" mb="16px">
                    Manage email notification preferences.
                  </Text>
                </Box>
                <Box>
                  {filteredEmailNotifications.map((type) => (
                    <Flex key={type} alignItems="center" justifyContent="space-between" mb="8px">
                      <Flex justifyContent="space-between" alignItems="center">
                        <Skeleton isLoaded={!isFetching} borderRadius="40px">
                          <Switch
                            isChecked={notificationSettings[type]}
                            onChange={() => handleNotificationChange(type, !notificationSettings[type])}
                            colorScheme="primary"
                          />
                        </Skeleton>
                        <Text textStyle="b-sm" color="neutral.500" ml="16px">
                          {capitalizeWords(type)}
                        </Text>
                      </Flex>
                    </Flex>
                  ))}
                </Box>
              </SimpleGrid>
            )}

          </Box>
        </ModalBody>
      </Box>
    </>
  )
}

export default NotificationSettingsPage
